import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const noop = () => {};

const ToastContext = createContext({ showToast: noop });

let toastIdSeq = 0;

function CelebrateIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/** Same glyph as Rank again (`flique-row-action-btn`): refresh / reorder arcs. */
function RankAgainToastIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" />
      <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14" />
    </svg>
  );
}

function toastVariantKeys(variantOpt) {
  if (variantOpt === 'celebrate') return 'celebrate';
  if (variantOpt === 'rerank') return 'rerank';
  return 'default';
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(id);
  }, []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  const showToast = useCallback(
    (message, options = {}) => {
      const id = ++toastIdSeq;
      const variant = toastVariantKeys(options.variant);
      const duration =
        variant === 'celebrate' || variant === 'rerank' ? 3200 : 2600;
      setToasts((prev) => [...prev, { id, message, variant, duration }]);
      const timer = setTimeout(() => removeToast(id), duration);
      timersRef.current.set(id, timer);
    },
    [removeToast],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="flique-toast-viewport"
        aria-live="polite"
        aria-relevant="additions text"
      >
        {toasts.map((t) => {
          const rich =
            t.variant === 'celebrate' || t.variant === 'rerank';
          return (
            <div
              key={t.id}
              className={`flique-toast${rich ? ' flique-toast--celebrate' : ''}`}
              role="status"
              style={{
                '--flique-toast-duration': `${t.duration}ms`,
              }}
            >
              {t.variant === 'celebrate' ? (
                <span className="flique-toast-celebrate-icon" aria-hidden="true">
                  <CelebrateIcon />
                </span>
              ) : null}
              {t.variant === 'rerank' ? (
                <span className="flique-toast-celebrate-icon" aria-hidden="true">
                  <RankAgainToastIcon />
                </span>
              ) : null}
              <span className="flique-toast-text">{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
